import React, { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl, fmtDate, sumValor, fmtMonthLong } from "./_shared/utils";
import { catById } from "./_shared/categories";
import { useAppData } from "./_shared/store";
import LancamentoModal from "./_shared/LancamentoModal";
import ConfirmDialog from "./_shared/ConfirmDialog";
import { Plus, Pencil, Trash2, Search, Copy, LayoutGrid } from "lucide-react";
import { toast } from "sonner";

const MESES_CURTOS = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const MESES_LONGOS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
const PAGAMENTOS   = ["Pix","Débito","Cartão","Dinheiro","Boleto","Transferência","Corretora","Outro"];

export default function GastosFixos() {
  const { data, updateLancamento, removeLancamento, removeLancamentoSerie, addLancamento } = useAppData();
  const ym   = data.selectedMonth;
  const year = ym.slice(0, 4);
  const all  = data.gastosFixos || [];
  const customCats = data.customCategories || [];


  /* ── Filters ── */
  const [q,            setQ]            = useState("");
  const [filterCat,    setFilterCat]    = useState("all");
  const [filterPag,    setFilterPag]    = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [monthFilter,  setMonthFilter]  = useState(null);

  /* ── Modal ── */
  const [modalOpen,  setModalOpen]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null, desc: "", seriesId: null });

  /* ── Replicar dialog ── */
  const [replicarOpen, setReplicarOpen] = useState(false);

  /* ── Active month ── */
  const activeYM = monthFilter || ym;

  /* ── Next month ── */
  const nextMonth = useMemo(() => {
    const [y, m] = activeYM.split("-").map(Number);
    if (m === 12) return `${y + 1}-01`;
    return `${y}-${String(m + 1).padStart(2, "0")}`;
  }, [activeYM]);

  /* ── Items ── */
  const monthItems = useMemo(
    () => all.filter((i) => (i.data || "").startsWith(activeYM)),
    [all, activeYM],
  );

  const filtered = useMemo(
    () =>
      monthItems
        .filter((i) => i.desc.toLowerCase().includes(q.toLowerCase()))
        .filter((i) => filterCat    === "all" || i.cat       === filterCat)
        .filter((i) => filterPag    === "all" || i.pagamento === filterPag)
        .filter((i) => filterStatus === "all" || (filterStatus === "pago" ? i.pago : !i.pago))
        .sort((a, b) => (a.data || "").localeCompare(b.data || "")),
    [monthItems, q, filterCat, filterPag, filterStatus],
  );

  /* ── Summary ── */
  const totalMes      = sumValor(monthItems);
  const totalPago     = sumValor(monthItems.filter((i) => i.pago));
  const totalPendente = sumValor(monthItems.filter((i) => !i.pago));

  /* ── Category cards ── */
  const catSummaries = useMemo(() => {
    const ids = [...new Set(monthItems.map((i) => i.cat).filter(Boolean))];
    return ids
      .map((catId) => {
        const cat = catById(catId, customCats);
        return { ...cat, total: sumValor(monthItems.filter((i) => i.cat === catId)) };
      })
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [monthItems, customCats]);

  /* ── 12-month cards ── */
  const monthCards = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => {
        const m     = String(i + 1).padStart(2, "0");
        const ymM   = `${year}-${m}`;
        const items = all.filter((it) => (it.data || "").startsWith(ymM));
        const total    = sumValor(items);
        const allPaid  = items.length > 0 && items.every((it) => it.pago);
        const somePaid = items.some((it) => it.pago);
        const status   = items.length === 0 ? "empty" : allPaid ? "pago" : somePaid ? "parcial" : "pendente";
        return { ym: ymM, mes: MESES_CURTOS[i], total, status, count: items.length };
      }),
    [all, year],
  );

  /* ── Categories in use (for filter dropdown) ── */
  const catsInUse = useMemo(() => {
    const ids = [...new Set(all.map((i) => i.cat).filter(Boolean))];
    return ids.map((id) => catById(id, customCats)).filter(Boolean);
  }, [all, customCats]);

  /* ── Handlers ── */
  const togglePago   = (id, cur)      => updateLancamento("fixo", id, { pago: !cur });
  const askRemove    = (id, desc, sid) => setConfirmDel({ open: true, id, desc, seriesId: sid || null });
  const openCreate   = ()             => { setEditing(null); setModalOpen(true); };
  const openEdit     = (it)           => { setEditing(it);   setModalOpen(true); };

  const doRemove = () => {
    if (confirmDel.id) {
      removeLancamento("fixo", confirmDel.id);
      setMonthFilter(null);
      toast.success(`"${confirmDel.desc || "Lançamento"}" removido`);
    }
  };

  const doRemoveSerie = () => {
    if (confirmDel.seriesId) {
      removeLancamentoSerie("fixo", confirmDel.seriesId);
      setMonthFilter(null);
      toast.success(`Série "${confirmDel.desc}" removida`);
    }
  };

  /* ── Replicar mês ── */
  const doReplicar = () => {
    const [ny, nm] = nextMonth.split("-").map(Number);
    monthItems.forEach((it) => {
      const day     = parseInt((it.data || "").slice(8, 10), 10) || 15;
      const lastDay = new Date(ny, nm, 0).getDate();
      const d       = Math.min(day, lastDay);
      const dataISO = `${ny}-${String(nm).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const { id: _id, seriesId: _sid, ...rest } = it;
      addLancamento("fixo", { ...rest, data: dataISO, pago: false, repetir: false });
    });
    setReplicarOpen(false);
    const n = monthItems.length;
    toast.success(`${n} lançamento${n > 1 ? "s" : ""} replicado${n > 1 ? "s" : ""} para ${MESES_LONGOS[nm - 1]}!`);
  };

  const subtitleText = monthFilter
    ? `${MESES_LONGOS[parseInt(activeYM.slice(5, 7), 10) - 1]} de ${year}`
    : fmtMonthLong(ym);

  return (
    <AppLayout>
      <PageHeader title="Gastos Fixos" subtitle={subtitleText} testid="fixos" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 space-y-6">

        {/* ── TOP BAR ── */}
        <div className="rounded-2xl p-4 md:p-5 bg-slate-900 border border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="fixos-summary">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Total do mês</div>
            <div className="mt-1 font-display text-2xl md:text-3xl font-bold text-white">{brl(totalMes)}</div>
            {totalPendente > 0 && (
              <div className="text-xs text-slate-400 mt-0.5">
                <span className="text-emerald-400">{brl(totalPago)}</span>{" pago · "}
                <span className="text-yellow-400">{brl(totalPendente)}</span>{" pendente"}
              </div>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => monthItems.length > 0 ? setReplicarOpen(true) : toast.error("Nenhum lançamento para replicar.")}
              className="h-11 px-4 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:border-slate-600 transition text-sm font-semibold inline-flex items-center gap-2"
              data-testid="fixos-replicar"
            >
              <Copy className="h-4 w-4" /> Replicar mês
            </button>
            <button
              onClick={openCreate}
              className="monyx-btn-primary text-white rounded-full h-11 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2"
              data-testid="fixos-add"
            >
              <Plus className="h-4 w-4" /> Novo lançamento
            </button>
          </div>
        </div>

        {/* ── CATEGORY SUMMARY CARDS ── */}
        {catSummaries.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="fixos-cat-cards">
            {catSummaries.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilterCat((v) => (v === cat.id ? "all" : cat.id))}
                className={`rounded-xl p-3 text-left border transition ${filterCat === cat.id ? "border-slate-500 bg-slate-800" : "bg-slate-900 border-slate-800 hover:border-slate-600"}`}
                style={{ borderTop: `3px solid ${cat.color}` }}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold truncate">{cat.name}</div>
                <div className="mt-1 text-sm font-display font-bold text-white tabular-nums">{brl(cat.total)}</div>
              </button>
            ))}
          </div>
        )}

        {/* ── 12-MONTH GRID ── */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 md:p-5" data-testid="fixos-12meses">
          <h3 className="font-display text-base font-bold mb-3 flex items-center gap-2">
            <LayoutGrid className="h-4 w-4 text-blue-400" />
            Visão do Ano {year}
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
            {monthCards.map((mc) => (
              <button
                key={mc.ym}
                onClick={() => setMonthFilter((v) => (v === mc.ym ? null : mc.ym))}
                className={`rounded-xl p-2.5 border text-center transition ${
                  monthFilter === mc.ym
                    ? "bg-blue-500/20 border-blue-500/50"
                    : mc.status === "empty"
                    ? "bg-slate-950/50 border-slate-800 opacity-40"
                    : "bg-slate-800/50 border-slate-700 hover:border-slate-500"
                }`}
              >
                <div className="text-[11px] font-bold text-slate-300" translate="no">{mc.mes}</div>
                {mc.status !== "empty" ? (
                  <>
                    <div className="text-[10px] font-bold text-white tabular-nums mt-0.5">{brl(mc.total)}</div>
                    <div className="text-[11px] mt-0.5">{mc.status === "pago" ? "✅" : "⏳"}</div>
                  </>
                ) : (
                  <div className="text-[10px] text-slate-600 mt-1">⬜</div>
                )}
              </button>
            ))}
          </div>
          {monthFilter && (
            <button
              onClick={() => setMonthFilter(null)}
              className="mt-3 text-xs text-slate-400 hover:text-blue-300 transition underline"
            >
              Limpar filtro de mês
            </button>
          )}
        </div>

        {/* ── FILTERS ── */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="fixos-filters">
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              placeholder="Buscar por descrição..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-10 pl-9 pr-4 rounded-full bg-slate-900 border border-slate-800 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/40"
              data-testid="fixos-search"
            />
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-blue-500/40"
          >
            <option value="all">Todas categorias</option>
            {catsInUse.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            value={filterPag}
            onChange={(e) => setFilterPag(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-blue-500/40"
          >
            <option value="all">Todos pagamentos</option>
            {PAGAMENTOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="h-10 rounded-full bg-slate-900 border border-slate-800 px-4 text-sm text-slate-300 focus:outline-none focus:border-blue-500/40"
          >
            <option value="all">Todos status</option>
            <option value="pago">Pago ✅</option>
            <option value="pendente">Pendente ⏳</option>
          </select>
        </div>

        {/* ── TABLE ── */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden" data-testid="fixos-table">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-slate-400 font-semibold border-b border-slate-800">
            <div className="col-span-3">Descrição</div>
            <div className="col-span-2">Categoria</div>
            <div className="col-span-2">Pagamento</div>
            <div className="col-span-1">Data</div>
            <div className="col-span-1 text-center">Status</div>
            <div className="col-span-2 text-right">Valor</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>
          <div className="divide-y divide-slate-800">
            {filtered.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">
                Nenhum lançamento. Clique em <b>Novo lançamento</b> para começar.
              </div>
            ) : (
              filtered.map((it) => {
                const cat = catById(it.cat, customCats);
                return (
                  <div key={it.id} className="px-4 md:px-5 py-3 hover:bg-slate-900/60 transition">
                    {/* Desktop */}
                    <div className="hidden md:grid grid-cols-12 items-center text-sm">
                      <div className="col-span-3 flex items-center gap-3 min-w-0">
                        <button
                          onClick={() => togglePago(it.id, it.pago)}
                          className={`h-5 w-5 rounded-full border-2 grid place-items-center shrink-0 transition ${it.pago ? "bg-emerald-500 border-emerald-500" : "border-slate-600 bg-transparent"}`}
                          data-testid={`row-${it.id}-pago`}
                          title={it.pago ? "Marcar como pendente" : "Marcar como pago"}
                        >
                          {it.pago && <svg viewBox="0 0 12 10" className="h-3 w-3"><path d="M1 5 L4.5 8.5 L11 1" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>}
                        </button>
                        <div className="min-w-0">
                          <div className="text-white truncate">{it.desc}</div>
                          {it.nota && <div className="text-[11px] text-slate-500 truncate italic">{it.nota}</div>}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${cat.bg} ${cat.text}`}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                          {cat.name}
                        </span>
                      </div>
                      <div className="col-span-2 text-slate-300 text-xs">{it.pagamento}</div>
                      <div className="col-span-1 text-slate-400 text-xs tabular-nums">{fmtDate(it.data)}</div>
                      <div className="col-span-1 text-center">
                        {it.pago
                          ? <span className="text-xs text-emerald-400 font-semibold">✅ Pago</span>
                          : <span className="text-xs text-yellow-400 font-semibold">⏳ Pendente</span>
                        }
                      </div>
                      <div className="col-span-2 text-right font-semibold tabular-nums text-white">
                        {brl(it.valor)}
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(it)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-blue-300 transition" data-testid={`row-${it.id}-edit`} title="Editar">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => askRemove(it.id, it.desc, it.seriesId)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-300 hover:text-rose-300 transition" data-testid={`row-${it.id}-del`} title="Remover">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden flex flex-col gap-2.5">
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => togglePago(it.id, it.pago)}
                          className={`h-5 w-5 mt-0.5 rounded-full border-2 grid place-items-center shrink-0 transition ${it.pago ? "bg-emerald-500 border-emerald-500" : "border-slate-600 bg-transparent"}`}
                          data-testid={`row-${it.id}-pago-m`}
                        >
                          {it.pago && <svg viewBox="0 0 12 10" className="h-3 w-3"><path d="M1 5 L4.5 8.5 L11 1" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <div className="text-white font-medium truncate">{it.desc}</div>
                            <div className="shrink-0 font-bold tabular-nums text-white">{brl(it.valor)}</div>
                          </div>
                          {it.nota && <div className="text-[11px] text-slate-500 italic mt-0.5">{it.nota}</div>}
                          <div className="mt-0.5 text-[11px] text-slate-400">
                            {it.pago ? "✅ Pago" : "⏳ Pendente"} · {fmtDate(it.data)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between flex-wrap gap-2 pl-8">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.bg} ${cat.text}`}>
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: cat.color }} />
                            {cat.name}
                          </span>
                          <span className="text-[11px] text-slate-300 px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700">{it.pagamento}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(it)} className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-blue-300 transition" data-testid={`row-${it.id}-edit-m`} title="Editar">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => askRemove(it.id, it.desc, it.seriesId)} className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800/70 text-slate-300 hover:text-rose-300 transition" data-testid={`row-${it.id}-del-m`} title="Remover">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>

      {/* ── REPLICAR DIALOG ── */}
      <Dialog open={replicarOpen} onOpenChange={setReplicarOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Copy className="h-5 w-5 text-blue-400" />
              Replicar para {MESES_LONGOS[parseInt(nextMonth.slice(5, 7), 10) - 1]}
            </DialogTitle>
            <DialogDescription>
              Os seguintes lançamentos serão copiados para o próximo mês com pago = Pendente:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-2 my-2">
            {monthItems.map((it) => {
              const cat = catById(it.cat, customCats);
              return (
                <div key={it.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-800/50 text-sm">
                  <div className="min-w-0 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: cat?.color || "#64748b" }} />
                    <div className="min-w-0">
                      <div className="text-white truncate">{it.desc}</div>
                      {it.nota && <div className="text-[11px] text-slate-500 italic truncate">{it.nota}</div>}
                    </div>
                  </div>
                  <span className="shrink-0 font-semibold text-white tabular-nums">{brl(it.valor)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setReplicarOpen(false)} className="flex-1">Cancelar</Button>
            <Button onClick={doReplicar} className="flex-1 monyx-btn-primary text-white font-semibold">
              Confirmar replicação
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <LancamentoModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        mode={editing ? "edit" : "create"}
        tipo="fixo"
        item={editing}
        defaultMonth={activeYM}
        onSaved={() => setMonthFilter(null)}
      />

      <ConfirmDialog
        open={confirmDel.open}
        onOpenChange={(v) => setConfirmDel((s) => ({ ...s, open: v }))}
        title="Remover lançamento?"
        description={`Deseja remover "${confirmDel.desc}"?${confirmDel.seriesId ? " Este lançamento faz parte de uma série recorrente." : ""}`}
        confirmLabel="Só este mês"
        onConfirm={doRemove}
        seriesLabel={confirmDel.seriesId ? "Remover toda a série" : undefined}
        onConfirmSeries={confirmDel.seriesId ? doRemoveSerie : undefined}
      />
    </AppLayout>
  );
}
